import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, FolderOpen, CheckCircle2, AlertTriangle, Users, Download, CalendarDays, X, LayoutGrid, RotateCcw, GripVertical, PieChart as PieIcon, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import api from "@/lib/api";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { StatoBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/time";
import { cn } from "@/lib/utils";
import { STATUS, STATUS_LABEL, STATUS_BAR, STATUS_HEX, PRIORITY, PRIORITY_BAR } from "@/lib/constants";
import { useStatuses } from "@/context/StatusContext";

const Grid = WidthProvider(GridLayout);
const LAYOUT_KEY = "flux_dash_layout_v2";
const DEFAULT_LAYOUT = [
  { i: "kpis", x: 0, y: 0, w: 12, h: 2, minW: 4, minH: 2 },
  { i: "status_chart", x: 0, y: 2, w: 5, h: 6, minW: 3, minH: 5 },
  { i: "by_status", x: 5, y: 2, w: 7, h: 3, minW: 3, minH: 3 },
  { i: "by_priority", x: 5, y: 5, w: 7, h: 3, minW: 3, minH: 3 },
  { i: "carico", x: 0, y: 8, w: 5, h: 4, minW: 3, minH: 3 },
  { i: "recenti", x: 5, y: 8, w: 7, h: 4, minW: 4, minH: 3 },
];

function BarRow({ label, value, max, colorClass, colorHex }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 shrink-0 truncate text-sm font-medium text-muted-foreground">{label}</span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full ${colorClass || ""}`} style={{ width: `${pct}%`, ...(colorHex ? { backgroundColor: colorHex } : {}) }} />
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-bold text-navy">{value}</span>
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange, testId }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const text = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${selected.length} selez.`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-full justify-between px-2 text-xs font-normal" data-testid={testId}>
          <span className="truncate">{text}</span>
          {selected.length > 0
            ? <span onClick={(e) => { e.stopPropagation(); onChange([]); }} data-testid={`${testId}-clear`} className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-secondary"><X size={12} /></span>
            : <ChevronsUpDown size={12} className="ml-1 shrink-0 opacity-60" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="max-h-60 space-y-0.5 overflow-y-auto">
          {options.length === 0 && <p className="px-1 py-2 text-xs text-muted-foreground">Nessuna opzione</p>}
          {options.map((o) => (
            <label key={o} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary" data-testid={`${testId}-opt-${o}`}>
              <Checkbox checked={selected.includes(o)} onCheckedChange={() => toggle(o)} />
              <span className="truncate">{o}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Tile({ title, icon: Icon, editing, children, testId }) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card" data-testid={testId}>
      <div className={cn("flex items-center gap-2 border-b border-border/60 px-5 py-3", editing && "cursor-move bg-secondary/40")}>
        {editing && <span className="tile-drag flex cursor-move items-center text-muted-foreground"><GripVertical size={16} /></span>}
        {Icon && <Icon size={16} className="text-primary" />}
        <h3 className="text-sm font-bold text-navy">{title}</h3>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-5">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { options: statusOptions, hexOf } = useStatuses();
  const [stats, setStats] = useState(null);
  const [dateFilters, setDateFilters] = useState([]);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(LAYOUT_KEY)); if (Array.isArray(s) && s.length) return s; } catch { /* noop */ }
    return DEFAULT_LAYOUT;
  });
  const [chartFilters, setChartFilters] = useState({ tecnico: [], categoria: [], committente: [] });
  const [chartDates, setChartDates] = useState([]);
  const navigate = useNavigate();

  const onLayoutChange = (l) => { setLayout(l); localStorage.setItem(LAYOUT_KEY, JSON.stringify(l)); };
  const resetLayout = () => { setLayout(DEFAULT_LAYOUT); localStorage.setItem(LAYOUT_KEY, JSON.stringify(DEFAULT_LAYOUT)); toast.success("Layout ripristinato"); };

  const pad2 = (n) => String(n).padStart(2, "0");
  const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const load = useCallback(() => {
    const params = {};
    if (dateFilters.length) params.dates = JSON.stringify(dateFilters.map(toISO));
    if (chartFilters.tecnico.length) params.c_tecnico = JSON.stringify(chartFilters.tecnico);
    if (chartFilters.categoria.length) params.c_categoria = JSON.stringify(chartFilters.categoria);
    if (chartFilters.committente.length) params.c_committente = JSON.stringify(chartFilters.committente);
    if (chartDates.length) params.c_dates = JSON.stringify(chartDates.map(toISO));
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
  }, [dateFilters, chartFilters, chartDates]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load);

  const tecniciOpts = stats?.options?.tecnici || [];
  const categorieOpts = stats?.options?.categorie || [];
  const committentiOpts = stats?.options?.committenti || [];

  const chartData = useMemo(() => {
    const cs = stats?.chart || { by_status: {}, total: 0, resa: 0, by_suspension_reason: {}, by_ko_reason: {} };
    const data = statusOptions.map((s) => ({ key: s.v, label: s.l, color: s.color, value: cs.by_status?.[s.v] || 0 }));
    return { data, total: cs.total || 0, resa: cs.resa || 0, suspByReason: cs.by_suspension_reason || {}, koByReason: cs.by_ko_reason || {} };
  }, [stats, statusOptions]);
  const chartSlices = chartData.data.filter((d) => d.value > 0);
  const chartTotal = chartData.total;
  const resaCounts = {
    completato: chartData.data.find((d) => d.key === "completato")?.value || 0,
    sospeso: chartData.data.find((d) => d.key === "sospeso")?.value || 0,
    ko: chartData.data.find((d) => d.key === "ko")?.value || 0,
  };
  const pctOfTotal = (n) => (chartTotal ? Math.round((n / chartTotal) * 1000) / 10 : 0);

  const kpi = {
    total: stats?.total || 0,
    completati: stats?.completati || 0,
    open: stats?.open || 0,
    overdue: stats?.overdue || 0,
    tecnici_count: stats?.tecnici_count || 0,
  };

  const derived = {
    byStatus: stats?.by_status || {},
    byPriority: stats?.by_priority || {},
    carico: stats?.carico || [],
    recenti: stats?.recenti || [],
  };

  const [drill, setDrill] = useState(null);
  const openDrill = async (kind, title) => {
    const params = { limit: 500 };
    if (dateFilters.length) params.dates = JSON.stringify(dateFilters.map(toISO));
    if (kind === "open") params.open_only = true;
    else if (kind === "completati") params.status = "completato";
    else if (kind === "overdue") params.overdue = true;
    setDrill({ title, items: null });
    try { const { data } = await api.get("/tickets", { params }); setDrill({ title, items: data }); }
    catch { setDrill({ title, items: [] }); }
  };

  const exportXlsx = async () => {
    try {
      const params = { limit: 5000 };
      if (dateFilters.length) params.dates = JSON.stringify(dateFilters.map(toISO));
      const { data: rowsT } = await api.get("/tickets", { params });
      const headers = ["Codice", "ID OLO", "Committente", "Cliente", "Comune", "Categoria", "Tecnico", "Appuntamento", "Stato", "Priorità", "Picco", "Creato"];
      const rows = rowsT.map((t) => [
        t.code || "", t.id_olo || "", t.committente || "", t.client_name || "", t.comune || "",
        t.category || "", t.assigned_to_name || "",
        t.scheduled_date ? `${fmtDate(t.scheduled_date)} ${t.appointment_time || ""}`.trim() : "",
        t.status || "", t.priority || "", t.picco || "", fmtDate(t.created_at),
      ]);
      const res = await api.post("/tickets/export-xlsx", { headers, rows }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `dashboard_ticket_${toISO(new Date())}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Esportati ${rows.length} ticket`);
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  const maxStato = Math.max(1, ...Object.values(derived.byStatus));
  const maxPri = Math.max(1, ...Object.values(derived.byPriority));
  const maxCarico = Math.max(1, ...derived.carico.map((c) => c.count));

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Gestione Ticket" title="Dashboard" subtitle="Panoramica operativa — trascina e ridimensiona le tile in modalità personalizza">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="dash-date-filter">
              <CalendarDays size={16} />
              {dateFilters.length === 0 ? "Tutte le date" : dateFilters.length === 1 ? fmtDate(toISO(dateFilters[0])) : `${dateFilters.length} giorni`}
              {dateFilters.length > 0 && (
                <span onClick={(e) => { e.stopPropagation(); setDateFilters([]); }} data-testid="dash-date-filter-clear" className="ml-1 rounded-full p-0.5 hover:bg-secondary"><X size={13} /></span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="multiple" selected={dateFilters} onSelect={(d) => setDateFilters(d || [])} />
            <div className="flex justify-between border-t border-border p-2">
              <Button variant="ghost" size="sm" onClick={() => setDateFilters([])}>Azzera</Button>
              <span className="self-center text-xs text-muted-foreground">{stats?.total ?? 0} ticket</span>
            </div>
          </PopoverContent>
        </Popover>
        {editing && <Button variant="ghost" onClick={resetLayout} data-testid="dash-reset-layout"><RotateCcw size={16} />Ripristina</Button>}
        <Button variant={editing ? "default" : "outline"} onClick={() => setEditing((e) => !e)} data-testid="dash-edit-toggle"><LayoutGrid size={16} />{editing ? "Fatto" : "Personalizza"}</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="export-btn"><Download size={16} />Esporta</Button>
      </PageHeader>

      <Grid
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={70}
        margin={[16, 16]}
        isDraggable={editing}
        isResizable={editing}
        draggableHandle=".tile-drag"
        onLayoutChange={onLayoutChange}
      >
        {/* KPI */}
        <div key="kpis" className={cn(editing && "ring-2 ring-primary/40 rounded-xl")}>
          <div className="flex h-full flex-col">
            {editing && <div className="tile-drag mb-2 flex cursor-move items-center gap-1 text-xs font-semibold text-primary"><GripVertical size={14} />Indicatori</div>}
            <div className="grid flex-1 grid-cols-2 gap-4 lg:grid-cols-5">
              {!stats ? (
                Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-full min-h-[100px] rounded-xl" />)
              ) : (
                <>
                  <StatCard testId="kpi-total" label="Ticket totali" value={kpi.total} icon={Ticket} tint="blue" onClick={() => openDrill("total", "Tutti i ticket")} />
                  <StatCard testId="kpi-aperti" label="Aperti" value={kpi.open} icon={FolderOpen} tint="amber" accent="text-primary" onClick={() => openDrill("open", "Ticket aperti")} />
                  <StatCard testId="kpi-completati" label="Completati" value={kpi.completati} icon={CheckCircle2} tint="green" accent="text-emerald-600" onClick={() => openDrill("completati", "Ticket completati")} />
                  <StatCard testId="kpi-ritardo" label="In ritardo" value={kpi.overdue} icon={AlertTriangle} tint="red" accent="text-red-600" onClick={() => openDrill("overdue", "Ticket in ritardo")} />
                  <StatCard testId="kpi-tecnici" label="Tecnici" value={kpi.tecnici_count} icon={Users} tint="purple" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* GRAFICO STATO FILTRABILE */}
        <div key="status_chart">
          <Tile title="Stato ticket" icon={PieIcon} editing={editing} testId="tile-status-chart">
            <div className="grid grid-cols-2 gap-2" onMouseDown={(e) => e.stopPropagation()}>
              <MultiSelect label="Tutti i tecnici" options={tecniciOpts} selected={chartFilters.tecnico} onChange={(v) => setChartFilters((f) => ({ ...f, tecnico: v }))} testId="chart-filter-tecnico" />
              <MultiSelect label="Tutte le categorie" options={categorieOpts} selected={chartFilters.categoria} onChange={(v) => setChartFilters((f) => ({ ...f, categoria: v }))} testId="chart-filter-categoria" />
              <MultiSelect label="Tutti i committenti" options={committentiOpts} selected={chartFilters.committente} onChange={(v) => setChartFilters((f) => ({ ...f, committente: v }))} testId="chart-filter-committente" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 w-full justify-between px-2 text-xs font-normal" data-testid="chart-filter-date">
                    <span className="flex items-center gap-1 truncate"><CalendarDays size={13} />{chartDates.length === 0 ? "Tutte le date" : chartDates.length === 1 ? fmtDate(toISO(chartDates[0])) : `${chartDates.length} giorni`}</span>
                    {chartDates.length > 0
                      ? <span onClick={(e) => { e.stopPropagation(); setChartDates([]); }} data-testid="chart-filter-date-clear" className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-secondary"><X size={12} /></span>
                      : <ChevronsUpDown size={12} className="ml-1 shrink-0 opacity-60" />}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar mode="multiple" selected={chartDates} onSelect={(d) => setChartDates(d || [])} />
                  <div className="flex justify-between border-t border-border p-2">
                    <Button variant="ghost" size="sm" onClick={() => setChartDates([])}>Azzera</Button>
                    <span className="self-center text-xs text-muted-foreground">{chartDates.length} selezionati</span>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="mt-2 min-h-[180px] flex-1" style={{ height: 200 }} data-testid="chart-status-donut">
              {chartTotal === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Nessun ticket per i filtri selezionati</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartSlices} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius="55%" outerRadius="82%" paddingAngle={2}>
                      {chartSlices.map((e) => <Cell key={e.key} fill={e.color || hexOf(e.key)} />)}
                    </Pie>
                    <RTooltip formatter={(v, n) => [`${v} ticket`, n]} />
                    <Legend iconType="circle" formatter={(val) => <span className="text-xs text-navy">{val}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <p className="mt-1 text-center text-xs text-muted-foreground">Totale: <span className="font-bold text-navy">{chartTotal}</span> ticket</p>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="dashboard-resa">
              <div className="flex flex-col rounded-lg border border-emerald-200 bg-emerald-50 p-2.5">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Completati</span>
                  <span className="text-xs font-extrabold text-emerald-700">{chartData.resa}%</span>
                </div>
                <span className="mt-0.5 text-2xl font-extrabold leading-none text-emerald-700">{resaCounts.completato}</span>
              </div>
              <div className="flex flex-col rounded-lg border border-red-200 bg-red-50 p-2.5" data-testid="dashboard-suspension-reasons">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-600">Sospesi</span>
                  <span className="text-xs font-extrabold text-red-600">{pctOfTotal(resaCounts.sospeso)}%</span>
                </div>
                <span className="mt-0.5 text-2xl font-extrabold leading-none text-red-600">{resaCounts.sospeso}</span>
                <div className="mt-1 space-y-0.5">
                  {Object.entries(chartData.suspByReason).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between gap-1 text-[11px] text-red-700" data-testid={`susp-reason-${reason}`}>
                      <span className="truncate">{reason}</span>
                      <span className="shrink-0 font-bold">{count} · {pctOfTotal(count)}%</span>
                    </div>
                  ))}
                  {resaCounts.sospeso === 0 && <span className="text-[11px] text-red-400">Nessuno</span>}
                </div>
              </div>
              <div className="flex flex-col rounded-lg border p-2.5" style={{ borderColor: "#7F1D1D40", backgroundColor: "#7F1D1D10" }} data-testid="dashboard-ko-reasons">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#7F1D1D" }}>KO</span>
                  <span className="text-xs font-extrabold" style={{ color: "#7F1D1D" }}>{pctOfTotal(resaCounts.ko)}%</span>
                </div>
                <span className="mt-0.5 text-2xl font-extrabold leading-none" style={{ color: "#7F1D1D" }}>{resaCounts.ko}</span>
                <div className="mt-1 space-y-0.5">
                  {Object.entries(chartData.koByReason).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between gap-1 text-[11px]" style={{ color: "#7F1D1D" }} data-testid={`ko-reason-${reason}`}>
                      <span className="truncate">{reason}</span>
                      <span className="shrink-0 font-bold">{count} · {pctOfTotal(count)}%</span>
                    </div>
                  ))}
                  {resaCounts.ko === 0 && <span className="text-[11px]" style={{ color: "#7F1D1D80" }}>Nessuno</span>}
                </div>
              </div>
            </div>
          </Tile>
        </div>

        {/* AVANZAMENTO PER STATO */}
        <div key="by_status">
          <Tile title="Avanzamento per stato" editing={editing} testId="tile-by-status">
            <div className="space-y-3">
              {stats ? statusOptions.map((s) => (
                <BarRow key={s.v} label={s.l} value={derived.byStatus[s.v] || 0} max={maxStato} colorClass={STATUS_BAR[s.v]} colorHex={STATUS_BAR[s.v] ? undefined : s.color} />
              )) : <Skeleton className="h-40" />}
            </div>
          </Tile>
        </div>

        {/* PRIORITA */}
        <div key="by_priority">
          <Tile title="Distribuzione per priorità" editing={editing} testId="tile-by-priority">
            <div className="space-y-3">
              {stats ? PRIORITY.map((p) => (
                <BarRow key={p.v} label={p.l} value={derived.byPriority[p.v] || 0} max={maxPri} colorClass={PRIORITY_BAR[p.v]} />
              )) : <Skeleton className="h-40" />}
            </div>
          </Tile>
        </div>

        {/* CARICO */}
        <div key="carico">
          <Tile title="Carico per tecnico" editing={editing} testId="tile-carico">
            <div className="space-y-3">
              {stats && derived.carico.length ? derived.carico.map((c) => (
                <BarRow key={c.nome} label={c.nome} value={c.count} max={maxCarico} colorClass="bg-primary" />
              )) : stats ? <p className="text-sm text-muted-foreground">Nessun dato disponibile</p> : <Skeleton className="h-32" />}
            </div>
          </Tile>
        </div>

        {/* RECENTI */}
        <div key="recenti">
          <Tile title="Ticket recenti" editing={editing} testId="tile-recenti">
            {stats && derived.recenti.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {["Codice", "Cliente", "Tecnico", "Stato"].map((h) => (
                      <th key={h} className="pb-2 pr-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {derived.recenti.map((t) => (
                    <tr key={t.id} onClick={() => !editing && navigate(`/tickets/${t.id}`)}
                      className={cn("border-b border-border/60 last:border-0", !editing && "cursor-pointer hover:bg-secondary/50")} data-testid={`recent-ticket-${t.code}`}>
                      <td className="py-2.5 pr-4 font-mono font-semibold text-primary">{t.code}</td>
                      <td className="py-2.5 pr-4 font-semibold text-navy">{t.client_name}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{t.assigned_to_name || "—"}</td>
                      <td className="py-2.5"><StatoBadge stato={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : stats ? <p className="py-8 text-center text-sm text-muted-foreground">Nessun ticket presente</p> : <Skeleton className="h-40" />}
          </Tile>
        </div>
      </Grid>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden" data-testid="kpi-drill-dialog">
          <DialogHeader><DialogTitle>{drill?.title} · {drill?.items?.length || 0}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Elenco dei ticket relativi all'indicatore selezionato</DialogDescription>
          <div className="-mx-2 max-h-[65vh] overflow-y-auto px-2">
            {drill && drill.items === null ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Caricamento...</p>
            ) : drill && drill.items.length ? (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-left">
                    {["Codice", "Cliente", "Committente", "Tecnico", "Appuntamento", "Stato"].map((h) => (
                      <th key={h} className="pb-2 pr-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drill.items.map((t) => (
                    <tr key={t.id} onClick={() => { setDrill(null); navigate(`/tickets/${t.id}`); }}
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-secondary/50" data-testid={`drill-ticket-${t.code}`}>
                      <td className="py-2.5 pr-4 font-mono font-semibold text-primary">{t.code}</td>
                      <td className="py-2.5 pr-4 font-semibold text-navy">{t.client_name || "—"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{t.committente || "—"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{t.assigned_to_name || "—"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{t.scheduled_date ? `${fmtDate(t.scheduled_date)} ${t.appointment_time || ""}`.trim() : "—"}</td>
                      <td className="py-2.5"><StatoBadge stato={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="py-10 text-center text-sm text-muted-foreground">Nessun ticket in questa categoria</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
